import { useState } from "react";
import { BOT_LEVELS, type BotLevel, type Side } from "@chess/shared";

import { ApiError, addBot } from "./api.js";

export interface AddBotControlProps {
  gameId: string;
  /** Which team the bot joins. */
  side: Side;
  label?: string;
  disabled?: boolean;
}

/**
 * Adds a bot to one team. Strength is picked here rather than per game, because
 * a democracy team is more interesting when its members disagree.
 */
export function AddBotControl({ gameId, side, label, disabled = false }: AddBotControlProps) {
  const [level, setLevel] = useState<BotLevel>(2);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleAdd() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await addBot(gameId, side, level);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Couldn't add a bot.");
    } finally {
      setBusy(false);
    }
  }

  const selected = BOT_LEVELS.find((entry) => entry.level === level);

  return (
    <div className="add-bot">
      <div className="add-bot__row">
        <select
          className="add-bot__level"
          value={level}
          disabled={disabled || busy}
          aria-label="Bot strength"
          onChange={(event) => setLevel(Number(event.target.value) as BotLevel)}
        >
          {BOT_LEVELS.map((entry) => (
            <option key={entry.level} value={entry.level}>
              {entry.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="button--secondary button--compact"
          disabled={disabled || busy}
          onClick={() => void handleAdd()}
        >
          {busy ? "Adding…" : (label ?? "Add bot")}
        </button>
      </div>
      <p className="footnote muted">{selected?.blurb}</p>
      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
